import { IncentiveData } from '@cp/src/reducers/leaderboardSlice';
import { getNumberFormat } from '@cp/src/utils/commonUtils';
import { Typography } from '@navi/web-ui/lib/primitives';
import styles from '../../Leaderboard.module.scss';
import cx from 'classnames';
import IncentiveMoneyIcon from '@cp/src/assets/icons/IncentiveMoneyIcon';

type INewSlabTooltipContent = {
  selectedIncentiveData?: IncentiveData;
};

const formatSlabLimits = (lowerLimit?: number, upperLimit?: number) => {
  const formatToLakh = (value: number) => {
    const formattedValue = (value / 100000).toFixed(2);
    return formattedValue.endsWith('.0') ? formattedValue.slice(0, -3) : formattedValue; // Remove trailing .0 if present
  };

  const formattedLowerLimit = lowerLimit !== undefined ? formatToLakh(lowerLimit) : '0';

  if (upperLimit) {
    const formattedUpperLimit = formatToLakh(upperLimit);
    return `₹${formattedLowerLimit}-${formattedUpperLimit}L`;
  } else {
    return `₹${formattedLowerLimit}L+`; // If upperLimit is not present
  }
};

const NewSlabTooltipContent = (props: INewSlabTooltipContent) => {
  const { selectedIncentiveData } = props;
  const toshowBufferCashCollected =
    (selectedIncentiveData?.bufferCashCollected ?? 0) > 0 &&
    (selectedIncentiveData?.bufferIncentiveEarned ?? 0) > 0;
  return (
    <div className="p-2 bg-[var(--grayscale-1)]">
      <div className="p-2 mb-2 rounded-[4px] bg-[var(--color-charcoal)] ">
        <div className="flex justify-between items-center">
          <Typography color="var(--text-primary)" variant="p5">
            Total cash collected
          </Typography>
          <Typography color="var(--text-primary)" variant="h4">
            {getNumberFormat(selectedIncentiveData?.totalCashCollected, 2) || `₹0`}
          </Typography>
        </div>
      </div>
      {toshowBufferCashCollected && (
        <div className="flex row justify-between m-1">
          <Typography className="italic" color="var(--grayscale-3)" variant="p5">
            * Buffer cash collected
          </Typography>
          <Typography className="italic" color="var(--grayscale-3)" variant="h4">
            {getNumberFormat(selectedIncentiveData?.bufferCashCollected, 2) || `₹0`}
          </Typography>
        </div>
      )}
      <div className="border-dashed border-[1px] border-[var(--greyscale-4)] mx-1 my-3" />
      <Typography color="var(--grayscale-3)" variant="p5" className="mx-1 mb-2 text-[10px]">
        INCENTIVE CALCULATION
      </Typography>
      <div className={cx(styles.incentiveText)}>
        <div>
          <div className="flex justify-between h-[36px]">
            <Typography color="var(--grayscale-3)" variant="p5" className="w-[77px] mt-2 ml-2">
              Slab
            </Typography>
            <Typography color="var(--grayscale-3)" variant="p5" className="w-[132px] mt-2 ml-2">
              Incentive percentage
            </Typography>
            <Typography
              color="var(--grayscale-3)"
              variant="p5"
              className="w-[110px] mt-2 mr-2 text-right"
            >
              Incentive earned
            </Typography>
          </div>

          {selectedIncentiveData?.slabBreakdown
            ?.slice()
            ?.sort((a, b) => a?.slabRank - b?.slabRank)
            .map((slab, index) => (
              <div
                key={index}
                className={cx('flex justify-between', styles.slabText, {
                  '!bg-[#0D412B]': slab.isCurrentSlab === true
                })}
              >
                <Typography
                  variant="p5"
                  color="var(--grayscale-3)"
                  className="w-[77px] mt-[10px] ml-2 mr-2 mb-[10px]"
                >
                  {formatSlabLimits(slab.slabLowerLimit, slab.slabUpperLimit)}
                </Typography>
                <Typography
                  variant="p5"
                  color="var(--navi-color-gray-c3)"
                  className="w-[132px] mt-[10px] ml-2 mr-2 mb-[10px]"
                  as="span"
                >
                  {`${slab.incentivePercentage}% `}
                  {slab.cashCollectedInSlab ? 'of' : ''}
                  <Typography as="span" variant="p5" color="var(--navi-color-gray-bg-primary)">
                    {slab.cashCollectedInSlab
                      ? ` ${getNumberFormat(slab.cashCollectedInSlab, 2)}`
                      : ''}
                  </Typography>
                </Typography>

                <Typography
                  variant="p5"
                  color="var(--navi-color-gray-bg-primary)"
                  className="w-[85px] mt-[10px] ml-2 mr-2 mb-[10px] text-right"
                >
                  {slab.incentiveInSlab >= 0 && slab.cashCollectedInSlab > 0 ? (
                    <>
                      {slab.incentiveInSlab > 0 ? '+' : ''}{' '}
                      {slab.incentiveInSlab === 0
                        ? ' ₹0'
                        : slab.incentiveInSlab * 100 < 1
                        ? ' ₹0'
                        : getNumberFormat(slab.incentiveInSlab, 2)}
                    </>
                  ) : (
                    '---'
                  )}
                </Typography>
              </div>
            ))}
        </div>
      </div>
      {toshowBufferCashCollected && (
        <div className="flex items-align mt-3 pr-2 pl-2 justify-between rounded-[4px] bg-[var(--brown-background-1)] items-center">
          <div className="flex flex-row gap-2 items-center">
            <IncentiveMoneyIcon />
            <Typography color="var(--text-primary)" variant="p5" className="pt-2  pb-2 ">
              Buffer incentive earned
            </Typography>
          </div>
          <Typography color="var(--text-primary)" variant="h4" className="pt-2 pb-2">
            {`+ `}
            {getNumberFormat(selectedIncentiveData?.bufferIncentiveEarned, 2) || ` ₹0`}
          </Typography>
        </div>
      )}
      <div className="flex items-align pr-2 pl-2 justify-between rounded-[4px] bg-[var(--color-charcoal)] mt-3">
        <Typography color="var(--text-primary)" variant="p5" className="pt-2  pb-2 ">
          Total incentive earned
        </Typography>
        <Typography color="var(--text-primary)" variant="h4" className="pt-2">
          {`= `}
          {getNumberFormat(selectedIncentiveData?.totalIncentiveEarned, 2) || ` ₹0`}
        </Typography>
      </div>
    </div>
  );
};

export default NewSlabTooltipContent;
